import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiErrorMessage } from '@/api'
import { authApi, type LoginPayload } from '@/api/auth'
import { workspacesApi } from '@/api/workspaces'
import type { User, Workspace } from '@/types/models'

const ACTIVE_WORKSPACE_STORAGE_KEY = 'sidedoor.active_workspace_id'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const workspaces = ref<Workspace[]>([])
  const activeWorkspaceId = ref<string | null>(
    localStorage.getItem(ACTIVE_WORKSPACE_STORAGE_KEY),
  )
  const bootstrapped = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => user.value !== null)
  const isSuperadmin = computed(() => user.value?.is_superadmin ?? false)
  const activeWorkspace = computed(
    () => workspaces.value.find((workspace) => workspace.id === activeWorkspaceId.value) ?? null,
  )

  function setActiveWorkspace(workspaceId: string | null) {
    activeWorkspaceId.value = workspaceId
    if (workspaceId) {
      localStorage.setItem(ACTIVE_WORKSPACE_STORAGE_KEY, workspaceId)
    } else {
      localStorage.removeItem(ACTIVE_WORKSPACE_STORAGE_KEY)
    }
  }

  async function fetchWorkspaces() {
    workspaces.value = await workspacesApi.index()
    if (!workspaces.value.some((workspace) => workspace.id === activeWorkspaceId.value)) {
      setActiveWorkspace(workspaces.value[0]?.id ?? null)
    }
  }

  /** Called once on app start to recover an existing session cookie, if any. */
  async function bootstrap() {
    if (bootstrapped.value) return
    try {
      user.value = await authApi.me()
      await fetchWorkspaces()
    } catch {
      user.value = null
    } finally {
      bootstrapped.value = true
    }
  }

  async function login(payload: LoginPayload) {
    loading.value = true
    error.value = null
    try {
      user.value = await authApi.login(payload)
      await fetchWorkspaces()
    } catch (e: unknown) {
      error.value = apiErrorMessage(e, 'Unable to sign in.')
      throw e
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    try {
      await authApi.logout()
    } finally {
      user.value = null
      workspaces.value = []
      setActiveWorkspace(null)
    }
  }

  async function createWorkspace(name: string): Promise<Workspace> {
    const workspace = await workspacesApi.store(name)
    workspaces.value = [...workspaces.value, workspace]
    setActiveWorkspace(workspace.id)
    return workspace
  }

  return {
    user,
    workspaces,
    activeWorkspaceId,
    bootstrapped,
    loading,
    error,
    isAuthenticated,
    isSuperadmin,
    activeWorkspace,
    setActiveWorkspace,
    fetchWorkspaces,
    bootstrap,
    login,
    logout,
    createWorkspace,
  }
})